import PianoWrapper from './PianoWrapper';

/**
 * Creates Piano event handlers with proper logging and integration.
 * Keep all caller-supplied callbacks in `handlers` untouched.
 *
 * @param {object} handlers – Custom hooks from the consumer (all optional)
 * @param {object} deps – shared utilities & refs from the provider
 */
export const createPianoEventHandlers = (
  handlers = {},
  deps,
) => {
  const {
    authenticate,
    trackEvent,
    pendingCheckoutRef,
    templateShownRef,
    userInteractedRef,
    startCheckout,
    authenticationState,
    getTinypassToken,
  } = deps;

  /**
   * Safely invokes a function if it is defined, passing any additional arguments to it.
   *
   * @param {Function} fn - The function to invoke.
   * @param {...*} args - Arguments to pass to the function.
   */
  const invokeIfDefined = (fn, ...args) => { if (typeof fn === 'function') fn(...args); };

  /**
   * Handles the start of the checkout process by tracking the event and invoking the corresponding handler.
   *
   * @param {object} e - The event object containing checkout details.
   * @param {string} [e.termId] - The identifier for the selected term (optional).
   * @param {string} [e.offerId] - The identifier for the selected offer (optional).
   */
  const handleCheckoutStart = (e) => {
    trackEvent('piano_checkout_start', { termId: e?.termId, offerId: e?.offerId });
    invokeIfDefined(handlers.onCheckoutStart, e);
  };

  /**
   * Handles the event when the checkout is closed.
   *
   * Tracks the 'piano_checkout_close' event with the current state and invokes the onCheckoutClose handler.
   *
   * @param {object} e - The event object containing checkout state information.
   * @param {string} e.state - The current state of the checkout when closed.
   */
  const handleCheckoutClose = (e) => {
    trackEvent('piano_checkout_close', { state: e.state });
    invokeIfDefined(handlers.onCheckoutClose, e);
  };

  /**
   * Handles changes in the checkout state by tracking the event and invoking the corresponding handler.
   *
   * @param {object} e - The event object containing information about the checkout state change.
   * @param {string} e.stateName - The name of the new checkout state.
   */
  const handleCheckoutStateChange = (e) => {
    trackEvent('piano_checkout_state_change', { state: e.stateName });
    invokeIfDefined(handlers.onCheckoutStateChange, e);
  };

  /**
   * Parses and extracts checkout parameters from a potentially nested input.
   *
   * @param {any} raw - The raw input that contains checkout parameters.
   * @returns {object|null} The parsed checkout parameters as an object, or null if parsing fails or the input is invalid.
   */
  const parseCheckoutParams = (raw) => {
    if (!raw) return null;

    // Drill down through any ".params" nesting Piano sometimes adds
    let value = raw;
    while (value && typeof value === 'object' && 'params' in value) {
      value = value.params;
    }

    // If we end up with a string, try JSON‑parsing once
    if (typeof value === 'string') {
      try {
        value = JSON.parse(value);
      } catch (err) {
        return null;
      }
    }

    // At this point value is either an object or something unusable
    return (typeof value === 'object') ? value : null;
  };

  /**
   * Handles custom checkout events triggered by the Piano integration.
   *
   * @param {object} event - The event object containing details about the custom event.
   * @param {string} event.eventName - The name of the custom event ('sign-in').
   * @param {object}[event.params] - Additional parameters associated with the event.
   * @fires authenticate - Initiates the authentication process if the user is not logged in.
   * @fires startCheckout - Starts the Piano checkout process if the user is logged in and parameters are provided.
   * @fires invokeIfDefined - Invokes the optional `onCheckoutCustomEvent` handler if defined.
   */
  const handleCheckoutCustomEvent = (event) => {
    userInteractedRef.current = true;
    trackEvent('piano_template_event', { name: event.eventName });

    if (event.eventName === 'sign-in') {
      const params = parseCheckoutParams(event.params);

      if (!window.tp?.userRef) {
        // Not logged in → open Bedrock modal first
        authenticate();
        if (params) pendingCheckoutRef.current = params;
      } else if (params) {
        // Logged in → jump straight to Piano checkout
        startCheckout(params);
      }
    }

    invokeIfDefined(handlers.onCheckoutCustomEvent, event);
  };

  /**
     * Handles the login required event for the Piano integration.
     * - If the user is already authenticated, starts the checkout process.
     * - If a template is shown but the user hasn't interacted, suppresses the login required action.
     * - Otherwise, tracks the event, closes any open Piano modals/offers, and triggers authentication.
     *
     * @param {object} params - Parameters related to the checkout or login event.
     * @returns {boolean} Always returns false to indicate further processing should stop.
     */
  const handleLoginRequired = (params) => {
    // If Piano has userRef, do nothing, otherwise the Piano script's onLoginRequired will handle the checkout
    if (window.tp?.userRef) {
      return true;
    }

    // No userRef - check if we can get it from Bedrock
    if (authenticationState === 'authenticated') {
      const token = getTinypassToken();
      if (token) {
        // Set the token and reload
        PianoWrapper.setUserAndReload(token);

        // Store params for retry after Piano reloads
        pendingCheckoutRef.current = params;

        return false;
      }
    }

    const storedOfferParams = PianoWrapper.getCurrentOfferParams();
    const completeParams = storedOfferParams ? {
      ...storedOfferParams,
      termId: params.termId || params.params?.termId,
    } : params;

    pendingCheckoutRef.current = completeParams;
    trackEvent('piano_login_required', completeParams);
    authenticate();
    return false;
  };

  /**
   * Handles the successful login event.
   *
   * Tracks the 'piano_login_success' event with the provided source,
   * and invokes the onLoginSuccess handler with the login data.
   *
   * @param {object} data - The login data.
   * @param {string} data.source - The source of the login event.
   */
  const handleLoginSuccess = (data) => {
    trackEvent('piano_login_success', { source: data.source });
    invokeIfDefined(handlers.onLoginSuccess, data);
  };

  /**
   * Handles the event when a piano template is shown.
   * Sets the templateShownRef to true, tracks the event, and calls the onShowTemplate handler.
   *
   * @param {object} params - The template data.
   * @param {string|number} params.templateId - The unique identifier of the template being shown.
   */
  const handleShowTemplate = (params) => {
    templateShownRef.current = true;
    trackEvent('piano_template_shown', { templateId: params.templateId });
    invokeIfDefined(handlers.onShowTemplate, params);
  };

  /**
   * Handles the display of a Piano offer by storing parameters and tracking the event.
   * @param {object} params - The offer parameters object
   * @param {string} params.offerId - The unique identifier for the offer being shown
   */
  const handleShowOffer = (params) => {
    // Store params for potential use in loginRequired
    PianoWrapper.setCurrentOfferParams(params);

    trackEvent('piano_offer_shown', { offerId: params.offerId });
    invokeIfDefined(handlers.onShowOffer, params);
  };

  /**
   * Handles the cancellation of a checkout process.
   * Clears the pending checkout reference, tracks the cancellation event,
   * and invokes the onCheckoutCancel handler if defined.
   */
  const handleCheckoutCancel = () => {
    pendingCheckoutRef.current = null;
    PianoWrapper.clearCurrentOfferParams();
    trackEvent('piano_checkout_cancel');
    invokeIfDefined(handlers.onCheckoutCancel);
  };

  /**
   * Handles checkout errors by resetting pending checkout state, tracking the error event,
   * and invoking the error handler if defined.
   * @param {Error} e - The error object containing details about the checkout failure
   */
  const handleCheckoutError = (e) => {
    pendingCheckoutRef.current = null;
    PianoWrapper.clearCurrentOfferParams();
    trackEvent('piano_checkout_error', { error: e.message });
    invokeIfDefined(handlers.onCheckoutError, e);
  };

  /**
   * Handles the completion of a checkout process in Piano.
   * Clears current offer parameters, tracks the checkout completion event,
   * and invokes the onCheckoutComplete handler if defined.
   *
   * @param {object} conversion - The conversion object containing checkout details
   * @param {string} conversion.termConversionId - The unique identifier for the term conversion
   * @param {number} conversion.chargeAmount - The amount charged for the conversion
   * @param {string} conversion.chargeCurrency - The currency code for the charge amount
   */
  const handleCheckoutComplete = (conversion) => {
    PianoWrapper.clearCurrentOfferParams();
    trackEvent('piano_checkout_complete', {
      termConversionId: conversion.termConversionId,
      chargeAmount: conversion.chargeAmount,
      chargeCurrency: conversion.chargeCurrency,
    });
    invokeIfDefined(handlers.onCheckoutComplete, conversion);
  };

  /**
   * Handles the execution of a piano experience event.
   * Tracks the 'piano_experience_execute' event with the associated experience ID,
   * and invokes the provided onExperienceExecute handler.
   *
   * @param {object} e - The event object containing experience execution details.
   * @param {object} e.result - The result object from the event.
   * @param {Array} [e.result.experiences] - Array of experience objects.
   * @param {string} [e.result.experiences.0.id] - The ID of the executed experience.
   */
  const handleExperienceExecute = (e) => {
    trackEvent('piano_experience_execute', { experienceId: e.result?.experiences?.[0]?.id });
    invokeIfDefined(handlers.onExperienceExecute, e);
  };

  return {
    handleCheckoutStart,
    handleCheckoutComplete,
    handleCheckoutClose,
    handleCheckoutCustomEvent,
    handleCheckoutStateChange,
    handleLoginRequired,
    handleLoginSuccess,
    handleShowTemplate,
    handleShowOffer,
    handleExperienceExecute,
    handleCheckoutCancel,
    handleCheckoutError,
  };
};
